import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { InputErrorAdornment } from 'components/common/InputErrorAdornment';
import dayjs from 'dayjs';
import { EventOccasion } from 'models/EventOccasion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EventSettingsProps {
  event?: Partial<EventOccasion>;
  isSaving?: boolean;
  onChange?(event: Partial<EventOccasion>): void;
}

export function EventSettings(props: EventSettingsProps) {
  const { t } = useTranslation();
  const { event } = props;
  const [showMore, setShowMore] = useState(false);

  const toggleShowMore = () => {
    setShowMore((prevShowMore) => !prevShowMore);
  };

  const handleChange = (values: Partial<EventOccasion>) => {
    const newEvent = { ...event, ...values };

    if (newEvent) {
      props.onChange?.(newEvent);
    }
  };

  useEffect(() => {
    if (event?.date) {
      const expireDate = dayjs(event.expireDate);
      const date = dayjs(event.date);

      if (!event?.expireDate || expireDate > date) {
        handleChange({
          expireDate: date.toISOString(),
        });
      }
    }
  }, [event?.date]);

  return (
    <Stack spacing={3}>
      <TextField
        required
        value={event?.name || ''}
        label={t('eventName', 'Event name')}
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => handleChange({ name: e.target.value })}
      />
      <DateTimePicker
        label={t('eventDate2', 'Event date *')}
        minDate={dayjs()}
        value={event?.date ? dayjs(event?.date) : null}
        sx={{ width: '100%' }}
        onChange={(value) => handleChange({ date: value?.toISOString() })}
      />
      <TextField
        label={t('personalNotes', 'Personal notes')}
        fullWidth
        multiline
        rows={3}
        value={event?.personalNotes}
        onChange={(e) => handleChange({ personalNotes: e.target.value })}
      />
      <FormControlLabel
        control={
          <Switch
            checked={event?.isLiveEvent ?? false}
            onChange={(_, checked) => handleChange({ isLiveEvent: checked })}
          />
        }
        label={t('liveEvent', 'Live Event')}
      />
      <Divider>
        <Button variant="contained" size="small" color="secondary" onClick={toggleShowMore}>
          {showMore ? (
            <>
              <Typography>{t('showLess', 'Show less')}</Typography>
              <ExpandLessIcon />
            </>
          ) : (
            <>
              <Typography>{t('showMore', 'Show more')}</Typography>
              <ExpandMoreIcon />
            </>
          )}
        </Button>
      </Divider>
      <Collapse in={showMore}>
        <Stack spacing={3}>
          <Typography variant="h3" gutterBottom>
            {t('requestSettings', 'Request Settings')}
          </Typography>
          <DateTimePicker
            label={t('acceptRequestUntil', 'Accept request until *')}
            value={event?.expireDate ? dayjs(event?.expireDate) : null}
            sx={{ width: '100%' }}
            onChange={(value) => {
              handleChange({ expireDate: value?.toISOString() });
            }}
            slots={{ inputAdornment: InputErrorAdornment }}
            slotProps={{
              inputAdornment: {
                hasError:
                  event?.date &&
                  event?.expireDate &&
                  dayjs(event?.expireDate).isAfter(dayjs(event?.date)),
              } as any,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={event?.disableRequests}
                onChange={(event, checked) => handleChange({ disableRequests: checked })}
              />
            }
            label={t('disableNewRequests', 'Disable new requests')}
          />
          {/* <TextField
            label="Max. requests per guest"
            fullWidth
            value={event?.maxRequestsPerUser}
            onChange={(e) => handleChange({ maxRequestsPerUser: parseInt(e.target.value) })}
            slotProps={{ htmlInput: { type: 'number', min: 1, max: 100, step: 5 } }}
          />
          <TextField
            label="Max. total requests"
            fullWidth
            value={event?.maxRequests}
            onChange={(e) => handleChange({ maxRequests: parseInt(e.target.value) })}
            slotProps={{ htmlInput: { type: 'number', min: 1, max: 100, step: 5 } }}
          />
          <FormControlLabel
            control={<Switch defaultChecked />}
            label="Allow requests from Spotify"
          />
          <FormControlLabel
            control={<Switch defaultChecked />}
            label="Allow text input as a request"
          /> */}
        </Stack>
      </Collapse>
    </Stack>
  );
}
